import {getDirPathForImageItem, getUrlForImageItemBigTierScreenSize, ImageItemSource} from '@Libraries/image-item.library';
import {UserImageSource} from '@Libraries/user-image-library';
import {MediaItemSizeType, repoURL} from '@Libraries/s3-library';
import {getWriteBucket} from '@Utils/s3.util';
import {downloadBlobAsFile} from '@Utils/file.util';
import {pollForPosterPreviewGenerationStatus} from '@Libraries/poster-library';
import {dispatchAiChatGeneratedAssetAdded} from '@AiChat/lib/ai-chat-assets-library';
import type {Dimensions} from '@Components/ai-chat-window/components/generated-image/generated-image.types';

const AI_CHAT_VIDEO_PREVIEW_POLLING_TIMEOUT = 300000;

export function getUserImageThumbUrl(hashedFilename: string, extension: string): string {
  return repoURL(`${getDirPathForImageItem(UserImageSource.UPLOAD, MediaItemSizeType.THUMB)}/${hashedFilename}.${extension}`, getWriteBucket());
}

export function getUserImageScreenBigUrl(hashedFilename: string): string {
  return getUrlForImageItemBigTierScreenSize(hashedFilename, ImageItemSource.UPLOAD);
}

const AI_REFERENCE_ATTACHMENTS_THUMB_FOLDER = 'aireferenceattachmentthumbs';

export function getAiReferenceAttachmentThumbUrl(hashedFilename: string, extension: string): string {
  return repoURL(`${AI_REFERENCE_ATTACHMENTS_THUMB_FOLDER}/${hashedFilename}.${extension}`, getWriteBucket());
}

export const getScaledDimensionsForImage = (naturalWidth: number, naturalHeight: number, maxWidth: number, maxHeight: number): Dimensions => {
  let displayWidth = naturalWidth;
  let displayHeight = naturalHeight;

  if (naturalHeight <= 0) {
    return {
      width: Math.min(naturalWidth, maxWidth),
      height: naturalHeight,
    };
  }

  const ratio = naturalWidth / naturalHeight;

  if (displayWidth > maxWidth) {
    displayWidth = maxWidth;
    displayHeight = maxWidth / ratio;
  }

  if (displayHeight > maxHeight) {
    displayHeight = maxHeight;
    displayWidth = maxHeight * ratio;
  }

  return {width: displayWidth, height: displayHeight};
};

export const getDimensionsFromAspectRatio = (aspectRatio: string, maxWidth: number, maxHeight: number): Dimensions => {
  const [widthStr, heightStr] = aspectRatio.split(':');
  const widthRatio = parseFloat(widthStr);
  const heightRatio = parseFloat(heightStr);

  if (!widthRatio || !heightRatio || heightRatio <= 0) {
    return {width: maxWidth, height: maxHeight};
  }

  const ratio = widthRatio / heightRatio;

  return getScaledDimensionsForImage(maxWidth, maxWidth / ratio, maxWidth, maxHeight);
};

export const getSkeletonDimensions = (
  width: number | undefined,
  height: number | undefined,
  aspectRatio: string | undefined,
  maxDimension: number
): Dimensions => {
  if (typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0) {
    return getScaledDimensionsForImage(width, height, maxDimension, maxDimension);
  }

  if (aspectRatio) {
    return getDimensionsFromAspectRatio(aspectRatio, maxDimension, maxDimension);
  }

  return {width: maxDimension, height: maxDimension};
};

interface AiChatImageAssetInitialResponse {
  key: string;
  pollingKey: string;
}

interface AiChatImageAssetPollingResponse {
  posterHashedId?: string;
}

interface AiChatAnimationAssetInitialResponse {
  key: string;
  pollingKey: string;
}

interface AiChatAnimationAssetPollingResponse {
  posterHashedId?: string;
}

export const getPosterHashedIdForAiChatAnimationAsset = async (animationHashedId: string): Promise<string> => {
  const initiateRequest = async (): Promise<AiChatAnimationAssetInitialResponse> => {
    return (await window.PMW.writeLocal('ai/getPosterForAiChatAnimationAsset', {
      animationHashedId,
    })) as AiChatAnimationAssetInitialResponse;
  };

  const checkStatus = async (response: AiChatAnimationAssetInitialResponse, immediate = false): Promise<AiChatAnimationAssetPollingResponse> => {
    return (await window.PMW.readLocal('ai/checkPosterCreationStatus', {
      key: response.key,
      ...(immediate ? {streamImmediate: 1} : {}),
    })) as Promise<AiChatAnimationAssetPollingResponse>;
  };

  const result = (await window.PMW.pollingAjaxCallAsync(initiateRequest, checkStatus)) as AiChatAnimationAssetPollingResponse;

  if (!result.posterHashedId) {
    throw new Error('Failed to create poster from AI chat animation asset');
  }

  const posterHashedId = result.posterHashedId;
  await new Promise<void>((resolve) => {
    pollForPosterPreviewGenerationStatus(
      posterHashedId,
      () => {
        resolve();
      },
      () => {
        resolve();
      },
      false,
      AI_CHAT_VIDEO_PREVIEW_POLLING_TIMEOUT
    );
  });

  return posterHashedId;
};

export const trimAiChatAnimation = async (animationHashedId: string, startTime: number, endTime: number, chatId: string): Promise<void> => {
  await window.PMW.writeLocal('ai/trimAiChatAnimation', {
    animationHashedId,
    startTime,
    endTime,
    chatId,
  });
};

export const downloadTrimmedAiChatAnimation = async (animationHashedId: string, removeAudio = false): Promise<void> => {
  const params = new URLSearchParams({animationHashedId});
  if (removeAudio) {
    params.set('removeAudio', '1');
  }
  const url = `${window.PMW.util.site_url('ai/downloadTrimmedAiChatAnimation')}?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Download failed: ${res.status}`);
  }
  const blob = await res.blob();
  downloadBlobAsFile(blob, `PosterMyWall_GeneratedVideo_${animationHashedId.substring(0, 8)}.mp4`);
};

export const getPosterHashedIdForAiChatImageAsset = async (hashedId: string): Promise<string> => {
  const initiateRequest = async (): Promise<AiChatImageAssetInitialResponse> => {
    return (await window.PMW.writeLocal('ai/getPosterForAiChatImageAsset', {
      imageHashedId: hashedId,
    })) as AiChatImageAssetInitialResponse;
  };

  const checkStatus = async (response: AiChatImageAssetInitialResponse, immediate = false): Promise<AiChatImageAssetPollingResponse> => {
    return (await window.PMW.readLocal('ai/checkPosterCreationStatus', {
      key: response.key,
      ...(immediate ? {streamImmediate: 1} : {}),
    })) as Promise<AiChatImageAssetPollingResponse>;
  };

  const result = (await window.PMW.pollingAjaxCallAsync(initiateRequest, checkStatus)) as AiChatImageAssetPollingResponse;

  if (!result.posterHashedId) {
    throw new Error('Failed to create poster from AI chat image asset');
  }

  dispatchAiChatGeneratedAssetAdded();
  return result.posterHashedId;
};
